<script lang="ts" context="module">
  type SwimlaneItem = FoodItem & {
    campaign_uuid: string;
    seeMore: boolean;
  };
  
  type SwimlaneType = 'item_merch';

  type BaseSwimlane = {
    type: SwimlaneType;
    sort: SortType;
  }

	export type ItemSwimlane = BaseSwimlane & {
    type: 'item_merch';
		swimlane: {
			swimlane_data: SwimlaneItem[];
			title_in_app: string;
		};
	};

  export type Swimlane = ItemSwimlane;

  export function mockItems(items: SwimlaneItem[]) {
    if (items.length > 0)
      while (items.length < 30) {
         items.push(...items); 
      }
    items.forEach((i, idx) => {
      if (idx % 2 == 0) {
        i.badge = "Buy 1 Get 1 Free";
      } else {
        i.old_price = 25;
      }
    })

    return items;
  }
</script>

<script lang="ts">
	import type { RestaurantDynamic, RestaurantSemiStatic, SortType } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	
	import { Vertical } from "$lib/def/verticals";
	import { get$ } from "$lib/serv/core/auth";
	import {firstValueFrom } from "rxjs";
	
	import type { RestaurantMap } from "$lib/ui/home/FoodLanding.svelte";
  import {KEY_ITEM_MERCH} from "$lib/serv/food-landing";
	import { truthy } from "$lib/util/func";
	import { query } from "$lib/util/url";
	import { navPDP } from "$lib/ui/home/food/ItemMerchGrid.svelte";

	import UIFoodItem, { type FoodItem } from "$lib/ui/home/food/FoodItem.svelte";
	import CarouselV3 from "$lib/ui/layout/CarouselV3.svelte";

  type Props = {
    title: string;
	  endpoint: string;
    restaurants: RestaurantMap,
	  searchParams: Record<string, string>;
  };
  const {searchParams, restaurants, endpoint, title}: Props = $props();
  const doResize = $derived(!!restaurants);

	let resize = () => {};

  $effect(() => {
    getSwimlane(endpoint, searchParams);
	  if (doResize) resize?.(); 
  });

  let cache = $state<Cache<ItemSwimlane>>({});
  const seeMoreImgs = $state<string[]>([]);

  async function getSwimlane(e_point: string, params: Record<string, string>) {
    if (!e_point) {
      console.error("Invalid endpoint: ", e_point);
      throw new Error("Invalid endpoint: " + e_point);
    }
    cache = {loading: true, payload: undefined};
    if (params.vertical === Vertical.Store && params.primary_food_filter) {
      e_point += `&classification=${params.primary_food_filter}`
    }
    const res = await firstValueFrom(get$<Swimlane>(`api:/${e_point}`));

    const items = (res.swimlane.swimlane_data ?? []).map((item) => {
      item.restaurant = {
        dynamic: restaurants.dynamic.get(item.restaurant_id) as RestaurantDynamic,
        semi_static: restaurants.semi_static.get(item.restaurant_id) as RestaurantSemiStatic,
      };
      item.seeMore = false;
      return item;
    })
    .filter((item) => truthy(item.restaurant.dynamic) && truthy(item.restaurant.semi_static));

    if (searchParams.mock) {
      mockItems(items);
    }

    const MAX_ITEMS = 15;
    if (items.length >= MAX_ITEMS) {
      const imageUrls = items.splice(14, items.length - MAX_ITEMS).map((i) => i.image_url);
      seeMoreImgs.push(...imageUrls);
      items.push({ seeMore: true } as SwimlaneItem);
    }
    res.swimlane.swimlane_data = items;

    cache = {loading: false, payload: res};
  }
</script>

{#snippet t()}
  <h2 id="item-merch-anchor" class="title headline-m bold">{cache.payload?.swimlane.title_in_app ?? title}</h2>
{/snippet}

<CarouselV3 cache={cache} type="aisle" key={(s?: ItemSwimlane) => s?.swimlane.swimlane_data ?? []} title={t}>
  {#snippet children({ item })}
    {#if item && item.seeMore}
      <a href={query(new URLSearchParams(searchParams)).set(KEY_ITEM_MERCH, "1").toUrl()} class="item">
        <div class="image" style="background-color: initial; padding: 4px;">
          <div class="see-more-tile">
            <div style={`background-image: url(${seeMoreImgs[0] || ''});`}></div>
            <div style={`background-image: url(${seeMoreImgs[1] || ''});`}></div>
            <div style={`background-image: url(${seeMoreImgs[2] || ''});`}></div>
            <div style={`background-image: url(${seeMoreImgs[3] || ''});`}></div>
          </div>
        </div>
        <div><span class="label-l bold m-0">See more...</span></div>
      </a>
    {/if}
    {#if !item.seeMore}
      <UIFoodItem item={item} on:click={() => navPDP(item, new URLSearchParams(searchParams))} />
    {/if}
  {/snippet}
</CarouselV3>

<style lang="scss">
  @use "color";

  .item {
    text-align: left;
  }

  .see-more-tile {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    border-radius: inherit;
    height: 100%;
    width: 100%;

    div {
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      background-image: url("https://picsum.photos/200/200");
      border-radius: inherit;
      outline: 1.2px solid color.$separator;
    }
  }
</style>
